import * as React from 'react';

interface ProductSwatchesSettings {
  // Layout Settings
  render_type?: 'all' | 'mobile-only' | 'desktop-only' | 'tablet-only' | 'ultrawide-only';

  // Swatch Settings
  swatch_data_type?: 'variants' | 'metafields' | 'metaobjects' | 'tags';
  source_type?: 'variants' | 'metafields' | 'metaobjects' | 'tags' | 'related_products';
  source_field?: string;
  metafield_format?: 'text' | 'json';
  action_type?: 'select_variant' | 'redirect_to_product';
  max_swatches?: number;
  swatches_wrap?: boolean;
  hide_scrollbar?: boolean;

  // Color Mapping (from schema: color_mapping)
  color_mapping?: string;

  // Appearance (from schema)
  swatch_style?: 'square' | 'circle';
  swatch_size?: number;
  swatch_size_mobile?: number; // Mobile-specific swatch size
  swatch_spacing?: number;
  swatch_spacing_mobile?: number; // Mobile-specific swatch spacing
  swatch_corner_radius?: number;
  swatch_border_width?: number;
  selected_border_width?: number;
  hover_scale?: number;

  // Colors (exact names from schema)
  background_color?: string;
  text_color?: string;
  swatch_border_color?: string;
  swatch_hover_border_color?: string;
  selected_border_color?: string;
  current_border_colour?: string; // Note: schema uses 'colour' for this one

  // Typography (from schema)
  title_font_size?: number;
  text_swatch_font_size?: number;

  // Swatch Type (from schema)
  swatch_type?: 'colour' | 'image' | 'color';

  // Display (from schema)
  show_name?: boolean;
  show_current_border?: boolean;
  whitespace_size?: number;

  // Animation Settings (from schema)
  enable_animations?: boolean;
  animation_type?:
    | 'fade-in'
    | 'slide-up'
    | 'slide-down'
    | 'slide-left'
    | 'slide-right'
    | 'slide-fade-up'
    | 'zoom-in'
    | 'zoom-out';
  animation_duration?: number;
  animation_delay?: number;
  animation_trigger?: 'onload' | 'onscroll'; // Schema only has these two options

  // Spacing (from schema)
  padding_top?: number;
  padding_bottom?: number;
  padding_left?: number;
  padding_right?: number;
  margin_top?: number;
  margin_bottom?: number;
  margin_left?: number;
  margin_right?: number;
  margin_top_mobile?: number;
  margin_bottom_mobile?: number;

  // Legacy/Backward compatibility properties
  colour_mapping?: string;
  swatch_shape?: 'default' | 'circle' | 'square';
  square_border_radius?: number;
  swatch_border_radius?: number;
  background_colour?: string;
  text_colour?: string;
  swatch_border_colour?: string;
  swatch_hover_border_colour?: string;
  selected_border_colour?: string;
  border_radius?: number;
}

interface SwatchItem {
  id?: string;
  title?: string;
  name?: string;
  url?: string;
  image?: string;
  color?: string;
  colour?: string;
  handle?: string;
  isSelected?: boolean;
  variantId?: string;
  variant_id?: string;
  is_product?: boolean;
  show_name?: boolean;
  // Additional product data for product switching
  product_id?: string;
  product_title?: string;
  product_handle?: string;
}

interface ProductSwatchesProps {
  settings: ProductSwatchesSettings;
  swatches?: SwatchItem[];
  currentColour?: string;
  currentSwatch?: string;
  swatchType?: 'colour' | 'image' | 'text';
  showName?: boolean;
  title?: string;
  product?: any;
  selectedVariantId?: string;
  onSwatchClick?: (swatch: SwatchItem) => void;
  isMobile?: boolean;
  // Options text props
  showOptionsText?: boolean;
  optionsText?: string;
  optionsTextStyle?: string;
}

export const ProductSwatches: React.FC<ProductSwatchesProps> = ({
  settings,
  isMobile,
  swatches = [],
  currentColour,
  currentSwatch,
  swatchType = 'colour',
  showName = false,
  title,
  product,
  selectedVariantId,
  onSwatchClick,
  showOptionsText = false,
  optionsText = '',
  optionsTextStyle = ''
}) => {
  const [selectedSwatch, setSelectedSwatch] = React.useState<string | null>();
  const [isAnimationReady, setIsAnimationReady] = React.useState(false);
  const swatchesContainerRef = React.useRef<HTMLDivElement>(null);

  // Initialize selected swatch based on current product state
  React.useEffect(() => {
    if (selectedVariantId) {
      setSelectedSwatch(selectedVariantId);
    } else if (currentColour && swatches.length > 0) {
      // Find swatch that matches current color
      const matchingSwatch = swatches.find(
        (s: SwatchItem) => {
          return s?.color === currentColour;
        }
      ) as SwatchItem | undefined;
      if (matchingSwatch) {
        setSelectedSwatch(matchingSwatch.id || matchingSwatch.variantId || matchingSwatch.color || '');
      }
    }
  }, [selectedVariantId, currentColour, swatches]);

  // Handle animations
  React.useEffect(() => {
    if (settings?.enable_animations) {
      const timer = setTimeout(() => {
        setIsAnimationReady(true);
      }, settings?.animation_delay || 0);
      return () => clearTimeout(timer);
    }
  }, [settings?.enable_animations, settings?.animation_delay]);

  // Add horizontal scroll with mouse wheel when swatches overflow
  React.useEffect(() => {
    const container = swatchesContainerRef.current;
    if (!container || settings?.swatches_wrap !== false) return;

    const handleWheelScroll = (e: WheelEvent) => {
      // Only handle horizontal scrolling when container can scroll horizontally
      if (container.scrollWidth > container.clientWidth) {
        e.preventDefault();
        
        // Convert vertical scroll (deltaY) to horizontal scroll
        const scrollAmount = e.deltaY || e.deltaX;
        container.scrollLeft += scrollAmount;
      }
    };

    container.addEventListener('wheel', handleWheelScroll, { passive: false });
    
    return () => {
      container.removeEventListener('wheel', handleWheelScroll);
    };
  }, [settings?.swatches_wrap]);

  // Add global protection against unwanted navigation for select_variant
  React.useEffect(() => {
    if (settings?.action_type !== 'select_variant') return;

    let isBlocking = false;

    const blockNavigation = (e: BeforeUnloadEvent) => {
      if (isBlocking) {
        e.preventDefault();
        e.returnValue = '';
        console.warn('Navigation blocked for select_variant action');
        return '';
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      // Only block for non-anchor elements in swatches
      if (target.closest('.product__swatches') && !target.closest('a')) {
        isBlocking = true;
        setTimeout(() => { isBlocking = false; }, 200);
      }
    };

    window.addEventListener('beforeunload', blockNavigation);
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('beforeunload', blockNavigation);
      document.removeEventListener('click', handleClick);
    };
  }, [settings?.action_type]);

  // Parse color mapping from settings (prioritize schema naming)
  const parseColourMapping = (colourMapping: string = ''): Record<string, string> => {
    const mapping: Record<string, string> = {};
    if (!colourMapping) return mapping;

    const pairs = colourMapping.split(',');
    pairs.forEach((pair) => {
      const [name, color] = pair.split(':');
      if (name && color) {
        mapping[name.trim().toLowerCase()] = color.trim();
      }
    });
    return mapping;
  };

  const colourMapping = parseColourMapping(settings?.color_mapping || settings?.colour_mapping);

  // Get color from name using mapping
  const getColorFromName = (name: string): string | null => {
    const lowerName = name.toLowerCase();
    for (const [colorName, colorValue] of Object.entries(colourMapping)) {
      if (lowerName.includes(colorName)) {
        return colorValue;
      }
    }
    return null;
  };

  // Get normalized setting values (prioritize schema property names, maintain backward compatibility)
  const getSwatchSize = () => isMobile ? settings?.swatch_size_mobile : settings?.swatch_size || 20;
  const getSwatchSpacing = () => isMobile ? settings?.swatch_spacing_mobile : settings?.swatch_spacing || 16;
  const getSwatchShape = () => settings?.swatch_style || settings?.swatch_shape || 'square';
  const getBorderRadius = () => {
    const shape = getSwatchShape();
    if (shape === 'circle') {
      return 50; // Make it circular
    } else if (shape === 'square') {
      return settings?.swatch_corner_radius || settings?.square_border_radius || settings?.swatch_border_radius || 0;
    }
    return settings?.swatch_corner_radius || settings?.swatch_border_radius || 4;
  };

  // Color getters using schema property names first, with backward compatibility
  const getTextColour = () => settings?.text_color || settings?.text_colour || '#000000';
  const getBorderColour = () => settings?.swatch_border_color || settings?.swatch_border_colour || '#e0e0e0';
  const getHoverBorderColour = () =>
    settings?.swatch_hover_border_color || settings?.swatch_hover_border_colour || '#666666';
  const getSelectedBorderColour = () =>
    settings?.selected_border_color || settings?.selected_border_colour || settings?.current_border_colour || '#AAAAAA';

  // Determine swatch shape classes
  const getShapeClasses = () => {
    const shape = getSwatchShape();
    switch (shape) {
      case 'circle':
        return 'rounded-full';
      case 'square':
        return `rounded-[${getBorderRadius()}px]`;
      default:
        return `rounded-[${getBorderRadius()}px]`;
    }
  };

  // Generate container styles
  const containerStyle: React.CSSProperties = {
    marginTop: `${settings?.margin_top || 8}px`,
    marginBottom: `${settings?.margin_bottom || 0}px`,
    marginLeft: `${settings?.margin_left || 0}px`,
    marginRight: `${settings?.margin_right || 0}px`,
    paddingTop: `${settings?.padding_top || 0}px`,
    paddingBottom: `${settings?.padding_bottom || 0}px`,
    paddingLeft: `${settings?.padding_left || 0}px`,
    paddingRight: `${settings?.padding_right || 0}px`
  };

  // Generate swatch styles
  const getSwatchStyle = (isSelected: boolean = false): React.CSSProperties => {
    const shouldShowBorder = isSelected && settings?.show_current_border !== false;

    return {
      width: `${getSwatchSize()}px`,
      height: `${getSwatchSize()}px`,
      borderWidth: shouldShowBorder
        ? `${settings?.selected_border_width}px`
        : `${settings?.swatch_border_width}px`,
      borderColor: isSelected ? getSelectedBorderColour() : getBorderColour(),
      borderStyle: shouldShowBorder ? 'solid' : 'none',
      transition: 'all 0.3s ease'
    };
  };

  // Animation classes
  const getAnimationClasses = () => {
    if (!settings?.enable_animations || !isAnimationReady) return '';

    const animationType = settings?.animation_type || 'slide-fade-up';
    const baseClasses = 'transition-all duration-300 ease-in-out';

    switch (animationType) {
      case 'fade-in':
        return `${baseClasses} animate-fade-in`;
      case 'slide-up':
        return `${baseClasses} animate-slide-up`;
      case 'slide-down':
        return `${baseClasses} animate-slide-down`;
      case 'slide-left':
        return `${baseClasses} animate-slide-left`;
      case 'slide-right':
        return `${baseClasses} animate-slide-right`;
      case 'slide-fade-up':
        return `${baseClasses} animate-slide-fade-up`;
      case 'zoom-in':
        return `${baseClasses} animate-zoom-in`;
      case 'zoom-out':
        return `${baseClasses} animate-zoom-out`;
      default:
        return baseClasses;
    }
  };

  // Render type visibility classes
  const getRenderTypeClasses = () => {
    switch (settings?.render_type) {
      case 'mobile-only':
        return 'block lg:hidden';
      case 'desktop-only':
        return 'hidden lg:block';
      case 'tablet-only':
        return 'hidden md:block lg:hidden';
      case 'ultrawide-only':
        return 'hidden xl:block';
      default:
        return '';
    }
  };

  const handleSwatchClick = (swatch: SwatchItem, event?: React.MouseEvent) => {
    // Prevent any default behavior that might cause navigation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (event.nativeEvent) {
        event.nativeEvent.stopImmediatePropagation();
      }
    }

    const swatchId = swatch.id || swatch.variantId || swatch.name || '';

    // Update the color name next to the swatch title
    const colorNameElement = document.querySelector('.product__swatch-name');

    // Handle different action types
    const actionType = settings?.action_type || 'select_variant';


    // Always update UI state first
    setSelectedSwatch(swatchId);

    if (colorNameElement) {
      colorNameElement.textContent = swatch.name || swatch.title || '';
    }

    // ONLY handle redirect for redirect_to_product action type
    if (actionType === 'redirect_to_product') {
      if (swatch.url) {
        window.location.href = swatch.url;
        return; // Exit early to prevent other logic
      }
    }

    // For select_variant action type, handle product/variant switching
    if (actionType === 'select_variant') {
      // Check if this is a product swatch (has product data) or a variant swatch
      if (swatch.handle && !swatch.variantId) {
        // This is a product swatch - dispatch product change event
        
        // Find the parent ProductTile container to target the event correctly
        const swatchElement = event?.target as HTMLElement;
        const parentProductTile = swatchElement?.closest('[id^="product-tile-"]');
        const targetProductTileId = parentProductTile?.id;
        
        if (targetProductTileId) {
          const productChangeEvent = new CustomEvent('product:change', {
            detail: { 
              productHandle: swatch.handle,
              swatch: swatch,
              targetProductTileId: targetProductTileId
            },
            bubbles: true
          });
          document.dispatchEvent(productChangeEvent);
        } else {
          console.warn('Could not find parent ProductTile for swatch click');
        }

      } else if (swatch.variantId) {
        // This is a variant swatch - only dispatch variant change event, don't modify form elements
        console.log('Dispatching variant:change for variant swatch:', swatch);
        
        // Dispatch variant change event
        const variantChangeEvent = new CustomEvent('variant:change', {
          detail: { variantId: swatch.variantId },
          bubbles: true
        });
        document.dispatchEvent(variantChangeEvent);
      }
      
      // Dispatch custom events for better integration
      const swatchChangeEvent = new CustomEvent('swatch:change', {
        detail: {
          swatch,
          swatchType,
          product,
          actionType,
          // Add additional context for different swatch types
          isProductSwatch: !!(swatch.handle && !swatch.variantId),
          isVariantSwatch: !!swatch.variantId,
          newProductHandle: swatch.handle
        },
        bubbles: true
      });
      document.dispatchEvent(swatchChangeEvent);
      
      // For select_variant, DO NOT call onSwatchClick and EXIT IMMEDIATELY
      console.log('Exiting early for select_variant action type');
      return false;
    }

    // Only execute this logic for non-select_variant action types
    if (onSwatchClick) {
      onSwatchClick(swatch);
    }

    // Dispatch custom events for better integration
    const swatchChangeEvent = new CustomEvent('swatch:change', {
      detail: {
        swatch,
        swatchType,
        product,
        actionType,
        // Add additional context for different swatch types
        isProductSwatch: !!(swatch.handle && !swatch.variantId),
        isVariantSwatch: !!swatch.variantId,
        newProductHandle: swatch.handle
      },
      bubbles: true
    });
    document.dispatchEvent(swatchChangeEvent);

    // Return false to prevent any further event propagation
    return false;
  };

  const isSelectedSwatch = (swatch: SwatchItem): boolean => {
    if (!selectedSwatch) return false;

    return selectedSwatch === swatch.id || selectedSwatch === swatch.variantId || selectedSwatch === swatch.color;
  };

  // Utility to check if a string is a valid hex color
  const isValidHexColor = (color: string): boolean => {
    return /^#([A-Fa-f0-9]{3}){1,2}$/.test(color.trim());
  };

  const renderSwatch = (swatch: SwatchItem, index: number) => {
    const isSelected = isSelectedSwatch(swatch);

    const swatchColor = swatch.colour || getColorFromName(swatch.name || swatch.title || '');
    const shapeClasses = getShapeClasses();
    const animationClasses = getAnimationClasses();

    // Handle animation trigger
    const getAnimationTriggerProps = () => {
      if (!settings?.enable_animations) return {};

      const triggerType = settings?.animation_trigger || 'onload';
      // Schema only supports 'onload' and 'onscroll', no 'onhover'
      if (triggerType === 'onscroll') {
        // For scroll trigger, we'd need intersection observer logic here
        // For now, treating as onload
        return {};
      }
      return {};
    };

    // Common props for both anchor and div
    const commonProps = {
      key: index,
      className: `relative cursor-pointer ${animationClasses}`,
      style: {
        animationDelay: `${(settings?.animation_delay || 0) + index * 100}ms`
      },
      ...getAnimationTriggerProps(),
      onMouseEnter: (e: React.MouseEvent) => {
        if (!isSelected) e.currentTarget.style.transform = `scale(${settings?.hover_scale || 1.1})`;
        const swatchElement = e.currentTarget.querySelector('.swatch-element') as HTMLElement;
        if (swatchElement) {
          swatchElement.style.borderColor = getHoverBorderColour();
        }
      },
      onMouseLeave: (e: React.MouseEvent) => {
        if (!isSelected) e.currentTarget.style.transform = 'scale(1)';
        const swatchElement = e.currentTarget.querySelector('.swatch-element') as HTMLElement;
        if (swatchElement) {
            swatchElement.style.borderColor = isSelected ? getSelectedBorderColour() : getBorderColour();
        }
      }
    };

    let swatchType = isValidHexColor(swatchColor || '') ? 'colour' : 'image';

    const swatchContent = (
      <div
        className={`swatch-element ${shapeClasses} ${
          isSelected ? 'border-[var(--border-color-override)]' : ''
        } overflow-hidden flex items-center justify-center`}
        style={getSwatchStyle(isSelected)}
      >
        {swatchType == 'image' && swatch.image ? (
          <img
            src={swatch.image}
            alt={swatch.title || swatch.name || ''}
            className={`w-full h-full object-cover ${shapeClasses}`}
            loading='lazy'
          />
        ) : swatchColor ? (
          <div
            className={`w-full h-full ${shapeClasses}`}
            style={{ backgroundColor: swatchColor }}
          />
        ) : (
          <div
            className='text-center font-medium'
            style={{
              fontSize: `${settings?.text_swatch_font_size || 10}px`,
              color: getTextColour(),
              lineHeight: 1
            }}
          >
            {(swatch.name || swatch.title || '').slice(0, 3)}
          </div>
        )}
      </div>
    );

    // Conditional rendering based on action type
    if (settings?.action_type === 'redirect_to_product' && swatch.url) {
      // Extract just the path from the full URL
      const getProductPath = (url: string): string => {
        try {
          const urlObj = new URL(url);
          return urlObj.pathname + urlObj.search; // Include query params like ?variant=...
        } catch {
          // If URL parsing fails, extract manually
          const match = url.match(/\/products\/[^?#]*(?:\?[^#]*)?/);
          return match ? match[0] : url;
        }
      };

      return (
        <a
          {...commonProps}
          href={getProductPath(swatch.url)}
        >
          {swatchContent}
        </a>
      );
    }

    return (
      <div
        {...commonProps}
        onClick={(e) => {
          handleSwatchClick(swatch, e);
        }}
      >
        {swatchContent}
      </div>
    );
  };

  // Process swatches based on settings
  const processedSwatches = React.useMemo(() => {
    let processedItems = [...swatches];

    // Limit swatches if max_swatches is set
    if (settings?.max_swatches) {
      processedItems = processedItems.slice(0, settings.max_swatches);
    }

    return processedItems;
  }, [swatches, settings?.max_swatches]);

  // Don't render if no swatches or render type doesn't match
  if (processedSwatches.length === 0) return null;

  return (
    <>
      {/* CSS for hiding scrollbars */}
      {settings?.swatches_wrap === false && settings?.hide_scrollbar && (
        <style>
          {`
            .scrollbar-hidden {
              scrollbar-width: none; /* Firefox */
              -ms-overflow-style: none; /* IE and Edge */
            }
            .scrollbar-hidden::-webkit-scrollbar {
              display: none; /* Safari and Chrome */
            }
          `}
        </style>
      )}
      
      <div
        className={`product__swatches-container flex flex-col gap-3 ${getRenderTypeClasses()}`}
        style={containerStyle}
        data-source-type={settings?.source_type}
        data-action-type={settings?.action_type}
      >
      {/* Swatches container with options text */}
      <div className="flex items-center gap-1">
        <div
          ref={swatchesContainerRef}
          className={`product__swatches flex ${settings?.swatches_wrap !== false ? 'flex-wrap' : 'flex-nowrap overflow-x-auto'} ${
            settings?.swatches_wrap === false && settings?.hide_scrollbar ? 'scrollbar-hidden' : ''
          }`}
          style={{
            gap: `${getSwatchSpacing()}px`
          }}
        >
          {/* Current swatch (if provided and different from variants) */}
          {currentSwatch && settings?.source_type !== 'variants' && (
            <div className='relative'>
              <div
                className={`${getShapeClasses()} overflow-hidden`}
                style={getSwatchStyle(true)}
              >
                {swatchType === 'image' ? (
                  <img
                    src={currentSwatch}
                    alt={currentColour || 'Current selection'}
                    className={`w-full h-full object-cover ${getShapeClasses()}`}
                  />
                ) : (
                  <div
                    className={`w-full h-full ${getShapeClasses()}`}
                    style={{ backgroundColor: currentSwatch }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Other swatches */}
          {processedSwatches.map((swatch, index) => {
            // Skip if this is the current swatch (for non-variant sources)
            if (
              currentSwatch &&
              settings?.source_type !== 'variants' &&
              (swatch.color === currentSwatch || swatch.colour === currentSwatch)
            ) {
              return null;
            }

            return renderSwatch(swatch, index);
          })}
        </div>

        {/* Options text beside swatches */}
        {showOptionsText && optionsText && (
          <span
            className={`${optionsTextStyle}`}
            style={{
              color: getTextColour()
            }}
          >
            {optionsText}
          </span>
        )}
      </div>
    </div>
    </>
  );
};
